// El BORRADOR de una plantilla: lo que el editor modifica mientras nadie
// guarda. Se arma con la respuesta del API, se edita con funciones puras y se
// vuelve a serializar en UN solo `PUT /estructura`.
//
// Por qué un borrador y no guardar cada gesto: reordenar, cambiar tres
// obligatoriedades y crear un ítem son un solo trabajo, y el servidor
// reemplaza la estructura entera. Un PUT por clic dejaría la plantilla
// a medias si el tercero falla, y haría imposible "Descartar".
//
// Un ítem LOCAL ("Solo en esta plantilla") es un ítem del maestro que dueña una
// sola plantilla hasta que se lo pasa al maestro. Se identifica por `ref`: el id
// del servidor como texto, o `tmp:…` si lo creó esta sesión y todavía no existe.

import {
  agregarFila,
  insertarDelMaestro,
  quitarFila,
  type Fila,
  type Obligatoriedad,
} from "./estructuraDePlantilla";
import type {
  DetalleDePlantilla,
  EstructuraAGuardar,
  FilaDeEstructura,
  LocalDeEstructura,
  MaestroItem,
  MaestroItemConUso,
  TipoDeContenido,
} from "./plantillas";
import type { ModoDeProduccion, RecursoDelDocumento } from "./produccionDelItem";

export interface ItemDeBorrador {
  ref: string;
  /** `null` mientras el ítem solo existe en el navegador. */
  id: number | null;
  /** Ubicación en el MAESTRO (la de la plantilla vive en la fila). */
  grupo: string;
  subgrupo: string | null;
  orden: number;
  nombre: string;
  descripcion: string;
  tipo: TipoDeContenido;
  fuente: string;
  condicionSugerida: string;
  frecuencia: number;
  ejemplo: string;
  local: boolean;
  /** "Pasar al maestro" pedido y todavía sin guardar. */
  publicar: boolean;
  /**
   * «Generación con IA», tal como lo confirmó el maestro. La plantilla NO lo
   * edita (se edita en el maestro): viaja en el borrador solo para mostrarlo.
   */
  modo: ModoDeProduccion | null;
  seccionOrigen: string;
  seRedactaAlFinal: boolean;
  textoFijo: string;
  recurso: RecursoDelDocumento | null;
  dependencias: { id: number; nombre: string }[];
}

export interface Borrador {
  filas: Fila[];
  items: Record<string, ItemDeBorrador>;
}

/** Los ítems nuevos van al final del maestro: nadie los ordena antes. */
const ORDEN_DE_UN_ITEM_NUEVO = Number.MAX_SAFE_INTEGER;

function itemDesdeElApi(item: MaestroItem): ItemDeBorrador {
  return {
    ref: String(item.id),
    id: item.id,
    grupo: item.grupo,
    subgrupo: item.subgrupo,
    orden: item.orden,
    nombre: item.nombre,
    descripcion: item.descripcion ?? "",
    tipo: item.tipo_contenido,
    fuente: item.fuente ?? "",
    condicionSugerida: item.condicion_sugerida ?? "",
    frecuencia: item.frecuencia,
    ejemplo: item.ejemplo ?? "",
    local: item.local,
    publicar: false,
    modo: item.modo_produccion,
    seccionOrigen: item.seccion_origen ?? "",
    seRedactaAlFinal: item.se_redacta_al_final,
    textoFijo: item.texto_fijo ?? "",
    recurso: item.recurso,
    dependencias: item.dependencias_detalle.map((d) => ({ ...d })),
  };
}

export function borradorDesdeElApi(detalle: DetalleDePlantilla): Borrador {
  const items: Record<string, ItemDeBorrador> = {};
  for (const item of detalle.items) items[String(item.id)] = itemDesdeElApi(item);

  return {
    filas: detalle.filas.map((fila) => ({
      item: fila.maestro_item_id === null ? null : String(fila.maestro_item_id),
      grupo: fila.grupo,
      subgrupo: fila.subgrupo,
      obligatoriedad: fila.obligatoriedad,
      condicion: fila.condicion ?? "",
    })),
    items,
  };
}

/** ¿Hay algo sin guardar? Compara contra lo último que devolvió el servidor. */
export function hayCambios(original: Borrador, actual: Borrador): boolean {
  return JSON.stringify(original) !== JSON.stringify(actual);
}

/* ------------------------------------------------------------------ */
/* Edición                                                             */
/* ------------------------------------------------------------------ */

function conFilas(b: Borrador, filas: Fila[]): Borrador {
  return { ...b, filas };
}

function mapearFila(b: Borrador, ref: string, cambio: (fila: Fila) => Fila): Borrador {
  return conFilas(
    b,
    b.filas.map((fila) => (fila.item === ref ? cambio(fila) : fila)),
  );
}

/** Crea un ítem local "Nuevo ítem" al final de su subgrupo, obligatorio. */
export function agregarItemLocal(
  b: Borrador,
  grupo: string,
  subgrupo: string | null,
  ref: string,
): Borrador {
  const item: ItemDeBorrador = {
    ref,
    id: null,
    grupo,
    subgrupo,
    orden: ORDEN_DE_UN_ITEM_NUEVO,
    nombre: "Nuevo ítem",
    descripcion: "",
    tipo: "texto",
    fuente: "Tasador",
    condicionSugerida: "",
    frecuencia: 0,
    ejemplo: "",
    local: true,
    publicar: false,
    modo: null,
    seccionOrigen: "",
    seRedactaAlFinal: false,
    textoFijo: "",
    recurso: null,
    dependencias: [],
  };

  return {
    filas: agregarFila(b.filas, {
      item: ref,
      grupo,
      subgrupo,
      obligatoriedad: "obligatorio",
      condicion: "",
    }),
    items: { ...b.items, [ref]: item },
  };
}

export function editarItem(
  b: Borrador,
  ref: string,
  cambios: Partial<
    Pick<ItemDeBorrador, "nombre" | "descripcion" | "tipo" | "fuente" | "condicionSugerida">
  >,
): Borrador {
  const actual = b.items[ref];
  if (!actual) return b;
  return { ...b, items: { ...b.items, [ref]: { ...actual, ...cambios } } };
}

/**
 * Cambia la obligatoriedad de un ítem en esta plantilla. Al volverse
 * condicional sin texto, propone la condición sugerida del maestro; una
 * condición ya escrita no se pisa (el servidor la descarta si no es condicional,
 * así que volver a "condicional" la recupera).
 */
export function cambiarObligatoriedad(
  b: Borrador,
  ref: string,
  obligatoriedad: Obligatoriedad,
): Borrador {
  return mapearFila(b, ref, (fila) => ({
    ...fila,
    obligatoriedad,
    condicion:
      obligatoriedad === "condicional" && !fila.condicion
        ? (b.items[ref]?.condicionSugerida ?? "")
        : fila.condicion,
  }));
}

export function cambiarCondicion(b: Borrador, ref: string, condicion: string): Borrador {
  return mapearFila(b, ref, (fila) => ({ ...fila, condicion }));
}

/** Sale de la plantilla. Un local se elimina del todo; uno compartido sigue en el maestro. */
/**
 * Los recursos del documento que piden dos o más ítems INCLUIDOS en la plantilla
 * (fila con obligatoriedad). El informe imprime un recurso una sola vez y el resto
 * remite a la primera sección, así que el mantenedor lo avisa. Se calcula sobre el
 * borrador y no sobre lo guardado: el aviso acompaña lo que se está editando.
 */
export function recursosRepetidos(borrador: Borrador): { recurso: RecursoDelDocumento; items: string[] }[] {
  const porRecurso = new Map<RecursoDelDocumento, string[]>();
  const vistos = new Set<string>();

  for (const fila of borrador.filas) {
    if (fila.item === null || fila.obligatoriedad === null || vistos.has(fila.item)) continue;
    vistos.add(fila.item);
    const item = borrador.items[fila.item];
    if (!item?.recurso) continue;
    porRecurso.set(item.recurso, [...(porRecurso.get(item.recurso) ?? []), item.nombre]);
  }

  return [...porRecurso.entries()]
    .filter(([, items]) => items.length > 1)
    .map(([recurso, items]) => ({ recurso, items }));
}

export function quitarItemDelBorrador(b: Borrador, ref: string): Borrador {
  const items = { ...b.items };
  if (items[ref]?.local) delete items[ref];
  return { filas: quitarFila(b.filas, ref), items };
}

/** Refs de los ítems locales que siguen en la plantilla sin pedir pasar al maestro. */
export function localesSinPasarAlMaestro(b: Borrador): string[] {
  return b.filas
    .map((fila) => fila.item)
    .filter((ref): ref is string => ref !== null)
    .filter((ref) => b.items[ref]?.local && !b.items[ref].publicar);
}

/** "Pasar al maestro": queda pendiente hasta guardar (el servidor lo hace ahí). */
export function pasarAlMaestro(b: Borrador, refs: string[]): Borrador {
  const items = { ...b.items };
  for (const ref of refs) {
    if (items[ref]?.local) items[ref] = { ...items[ref], publicar: true };
  }
  return { ...b, items };
}

/** Agrega ítems del maestro, cada uno en su posición natural. */
export function agregarDelMaestro(
  b: Borrador,
  elegidos: MaestroItemConUso[],
  obligatoriedad: Obligatoriedad,
): Borrador {
  let resultado: Borrador = { ...b, items: { ...b.items } };

  for (const elegido of elegidos) {
    const item = itemDesdeElApi(elegido);
    resultado.items[item.ref] = item;
    resultado = {
      ...resultado,
      filas: insertarDelMaestro(
        resultado.filas,
        { ref: item.ref, grupo: item.grupo, subgrupo: item.subgrupo },
        obligatoriedad,
        item.condicionSugerida,
        (ref) => resultado.items[ref]?.orden ?? ORDEN_DE_UN_ITEM_NUEVO,
      ),
    };
  }

  return resultado;
}

/* ------------------------------------------------------------------ */
/* Hacia el API                                                        */
/* ------------------------------------------------------------------ */

const esNuevo = (ref: string) => ref.startsWith("tmp:");

/**
 * Lo que viaja en `PUT /estructura`. Solo se mandan los ítems locales que
 * siguen en la plantilla: el servidor borra los demás, y eso es lo que significa
 * "Eliminar ítem" en un ítem local. La condición va solo si es condicional.
 */
export function payloadDeLaEstructura(b: Borrador): EstructuraAGuardar {
  const filas: FilaDeEstructura[] = [];
  const locales: LocalDeEstructura[] = [];
  const vistos = new Set<string>();

  for (const fila of b.filas) {
    const subgrupo = fila.subgrupo === null ? {} : { subgrupo: fila.subgrupo };

    if (fila.item === null || fila.obligatoriedad === null) {
      filas.push({ grupo: fila.grupo, ...subgrupo });
      continue;
    }

    const ref = fila.item;
    filas.push({
      ...(esNuevo(ref) ? { clave: ref } : { maestro_item_id: Number(ref) }),
      grupo: fila.grupo,
      ...subgrupo,
      obligatoriedad: fila.obligatoriedad,
      ...(fila.obligatoriedad === "condicional" && fila.condicion
        ? { condicion: fila.condicion }
        : {}),
    });

    const item = b.items[ref];
    if (item?.local && !vistos.has(ref)) {
      vistos.add(ref);
      locales.push({
        ...(item.id === null ? { clave: ref } : { id: item.id }),
        nombre: item.nombre,
        descripcion: item.descripcion,
        tipo_contenido: item.tipo,
        ...(item.fuente ? { fuente: item.fuente } : {}),
        ...(item.publicar ? { publicar: true } : {}),
      });
    }
  }

  return { filas, locales };
}
